import { INPUT_TYPE } from '@/shared/config';
import CloseIcon from '@/shared/ui/icons/Components/Common/close-icon.svg';
import classnames from 'classnames';
import _ from 'lodash';
import { Modal } from 'reactstrap';
import { IconButton, NormalButton } from '@/shared/ui';
import HintComments from '@/shared/ui/components/HintComments';
import DatePickerInput from '@/shared/ui/components/Inputs/datePickerInput';
import DropdownInput from '@/shared/ui/components/Inputs/dropdownInput';
import MutliSelectCheckbox from '@/shared/ui/components/Inputs/mutliSelectCheckbox';
import SelectInput from '@/shared/ui/components/Inputs/selectInput';
import TextInput from '@/shared/ui/components/Inputs/textInput';
import ToggleInput from '@/shared/ui/components/Inputs/toggleInput';
import useEditModal from './useEditModal';

export type EditModalProps = {
  customInputClassName?: string;
  className?: string;
  oneInputPerLine?: boolean;
  header: string;
  isSavePermissible?: boolean;
  isOpen: boolean;
  isSaving?: boolean;
  toggle: (...args: any[]) => void;
  onClickSave: () => void;
  // PropTypes declares a flat array of { title, options, value, type, isDifferent }
  // shapes, but when isMultiStep is true the caller passes an array of arrays
  // (one per step) — see inputList[0][0] below — so this stays untyped.
  inputList: any;
  isTextareaInput?: boolean;
  isHintComment?: boolean;
  // The third argument's meaning varies by which input type renders a given
  // list item (isValid flag, size, or index) — kept loose to match that.
  onChange?: (name: string, value: any, extra?: any) => void;
  maxChar?: number;
  buttonName?: string;
  isMultiStep?: boolean;
  messageWarning?: string;
  hasLeftButton?: boolean;
  leftButtonName?: string;
  onClickLeftButton?: () => void;
};

/**
 * Renders the shared edit dialog used across the report app for form-based record editing. Delegates draft state, dirty tracking, step navigation and field changes to useEditModal, then lays out a header with a close button, an optional warning message, the generated field list, and a footer whose primary button switches between Next and Save and can be preceded by Back and an optional destructive left button.
 *
 * @param props - Modal configuration: header, open state, field descriptors, save and toggle handlers, plus the layout, permission and multi-step flags.
 * @returns The rendered edit modal with its header, field list and footer buttons.
 */
const EditModal = ({
  customInputClassName = '',
  className,
  oneInputPerLine = false,
  header,
  isSavePermissible = true,
  isOpen,
  isSaving = false,
  toggle,
  onClickSave,
  inputList,
  isTextareaInput = false,
  isHintComment = false,
  onChange = () => {},
  maxChar,
  buttonName,
  isMultiStep = false,
  messageWarning,
  hasLeftButton = false,
  leftButtonName = '',
  onClickLeftButton = () => {},
}: EditModalProps) => {
  const {
    state,
    isDifferent,
    onClickBack,
    handleClickSave,
    onClickCloseButton,
    onChangeField,
    onChangeReportDueDate,
  } = useEditModal({
    isMultiStep,
    inputList,
    isOpen,
    onClickSave,
    toggle,
    maxChar,
    header,
    onChange,
  });

  /**
   * Renders a modal's form fields from a declarative list of input descriptors. Slices the list by step in multi-step mode, then maps each descriptor to the control named by its type - text, textarea, paired text, dropdown, multi-select checkbox, toggle, date picker, select, or a plain warning line - with the textarea and hint-comment flags overriding that switch, and disables every control while a save is in flight.
   *
   * @param inputList - The field descriptors to render, or one descriptor array per step in multi-step mode.
   * @param isTextareaInput - Renders every field as a textarea, bypassing the per-field type switch.
   * @param isHintComment - Renders the fields as a hint-comments editor instead of ordinary inputs.
   * @param isSaving - Disables all controls while the modal is saving.
   * @param isMultiStep - Treats inputList as one descriptor array per step.
   * @param step - Index of the step whose fields to render in multi-step mode.
   * @returns The rendered field list wrapped in its layout container.
   */
  const inputListRenderer = (
    inputList: any,
    {
      isTextareaInput = false,
      isHintComment = false,
    }: { isTextareaInput?: boolean; isHintComment?: boolean },
    isSaving: boolean,
    isMultiStep: boolean,
    step: number,
  ) => (
    <div
      className={classnames(
        oneInputPerLine ? 'input-list-one-per-line' : 'input-list',
        customInputClassName,
      )}
    >
      {_.map(isMultiStep ? inputList[step] : inputList, (item: any, index: number) => {
        let inputHtml = null;

        if (isTextareaInput && item.type !== INPUT_TYPE.TEXT_WARNING) {
          inputHtml = (
            <TextInput
              key={index}
              className={classnames('input-list-item-textarea', item.className)}
              name={item.name}
              type="textarea"
              label={item.label}
              disabled={isSaving || item.disabled}
              value={item.value}
              onChange={onChangeField}
              isError={!!item.error}
              errorMessage={item.error}
              placeholder={item.placeholder}
            />
          );
        } else if (isHintComment) {
          inputHtml = (
            // <HintComments
            //   id="modal-comments-drawer"
            //   comments={item.value}
            //   className="Event__input-comment__height hint-comments__component"
            //   updateComments={onChange}
            // />
            <HintComments
              className="Event__input-comment__height hint-comments__component"
              comments={item.value}
              updateComments={onChangeField}
            />
          );
        } else {
          switch (item.type) {
            case INPUT_TYPE.OPTION_MUTIL_CHOOSE: {
              inputHtml = (
                <MutliSelectCheckbox
                  key={index}
                  className="input-list-item"
                  disabled={isSaving || item.disabled}
                  label={item.label}
                  options={item.options}
                  value={item.value}
                  onChange={onChangeField}
                />
              );
              break;
            }
            case INPUT_TYPE.DROPDOWN_INPUT: {
              inputHtml = (
                <DropdownInput
                  key={index}
                  className="input-list-item"
                  disabled={isSaving || item.disabled}
                  name={item.name}
                  label={item.label}
                  options={item.options}
                  value={item.value}
                  onChange={onChangeField}
                />
              );
              break;
            }
            case INPUT_TYPE.DOUBLE_TEXT_INPUT: {
              inputHtml = (
                <div className="w-100 input-list-item">
                  <span>{item.label}</span>
                  <div className="flex mt1 justify-between">
                    <TextInput
                      className={classnames('w-100 mr1', item.className)}
                      disabled={isSaving || item.disabled}
                      name={item.name1}
                      type={item.inputType}
                      value={item.value1}
                      placeholder={item.placeholder}
                      onChange={onChangeField}
                      isError={!!item.error1}
                      isDateMask={item.isDateMask}
                      isPhoneNumberMask={item.isPhoneNumberMask}
                      messageBoxContent={item.messageBoxContent}
                      linkText={item.linkText}
                      handleClickLinkText={item.handleClickLinkText}
                      isNumberFormat={item.isNumberFormat}
                      suffix={item.suffix1}
                      decimalSeparator={item.decimalSeparator}
                    />
                    <TextInput
                      className={classnames('w-100 ml1', item.className)}
                      disabled={isSaving || item.disabled}
                      name={item.name2}
                      type={item.inputType}
                      value={item.value2}
                      placeholder={item.placeholder}
                      onChange={onChangeField}
                      isError={!!item.error2}
                      isDateMask={item.isDateMask}
                      isPhoneNumberMask={item.isPhoneNumberMask}
                      messageBoxContent={item.messageBoxContent}
                      linkText={item.linkText}
                      handleClickLinkText={item.handleClickLinkText}
                      isNumberFormat={item.isNumberFormat}
                      suffix={item.suffix2}
                      decimalSeparator={item.decimalSeparator}
                    />
                  </div>
                  {(!!item.error1 || !!item.error2) && (
                    <div className="error-message">{item.error1 || item.error2}</div>
                  )}
                </div>
              );
              break;
            }
            case INPUT_TYPE.TEXT_INPUT: {
              inputHtml = (
                <TextInput
                  key={index}
                  className={classnames('input-list-item', item.className)}
                  disabled={isSaving || item.disabled}
                  name={item.name}
                  type={item.inputType}
                  label={item.label}
                  value={item.value}
                  placeholder={item.placeholder}
                  onChange={onChangeField}
                  isError={!!item.error}
                  errorMessage={item.error}
                  isDateMask={item.isDateMask}
                  isPhoneNumberMask={item.isPhoneNumberMask}
                  messageBoxContent={item.messageBoxContent}
                  linkText={item.linkText}
                  handleClickLinkText={item.handleClickLinkText}
                  isNumberFormat={item.isNumberFormat}
                  suffix={item.suffix}
                  decimalSeparator={item.decimalSeparator}
                />
              );
              break;
            }
            case INPUT_TYPE.TEXT_AREA_INPUT: {
              inputHtml = (
                <TextInput
                  key={index}
                  className={classnames('input-list-item-textarea', item.className)}
                  name={item.name}
                  type="textarea"
                  label={item.label}
                  disabled={isSaving || item.disabled}
                  value={item.value}
                  onChange={onChangeField}
                  isError={!!item.error}
                  errorMessage={item.error}
                  placeholder={item.placeholder}
                />
              );
              break;
            }
            case INPUT_TYPE.TOGGLE_INPUT: {
              inputHtml = (
                <ToggleInput
                  key={index}
                  className="input-list-item"
                  disabled={isSaving || item.disabled}
                  name={item.name}
                  label={item.label}
                  options={item.options}
                  value={item.value}
                  onChange={onChangeField}
                  isError={!!item.error}
                  errorMessage={item.error}
                />
              );
              break;
            }
            case INPUT_TYPE.DATE_PICKER_INPUT: {
              inputHtml = (
                <DatePickerInput
                  className="input-list-item"
                  name={item.name}
                  label={item.label}
                  placeholder="MM/DD/YYYY"
                  value={item.value}
                  disabled={item.disabled}
                  belowLimit={item.belowLimit}
                  onChange={(date: any, dateString: any) => {
                    onChangeReportDueDate(item.name, date, dateString);
                  }}
                  isError={!!item.error}
                  errorMessage={item.error}
                  isWarning={!!item.warningMessage}
                  warningMessage={item.warningMessage}
                />
              );
              break;
            }
            case INPUT_TYPE.SELECT_INPUT: {
              inputHtml = (
                <SelectInput
                  isMulti={item.isMulti}
                  isSearchable={item.isSearchable}
                  isClearable={item.isClearable}
                  className={classnames('input-list-item', item.className)}
                  name={item.name}
                  label={item.label}
                  options={item.options}
                  placeholder={item.placeholder}
                  handleClickAdd={item.handleClickAdd}
                  value={item.value}
                  onChange={
                    item.name === 'country' || item.name === 'state' ? item.onChange : onChangeField
                  }
                  isError={!!item.error}
                  errorMessage={item.error}
                  disabled={isSaving || item.disabled}
                  messageBoxContent={item.messageBoxContent}
                  holterAndFollowOnContent={item.holterAndFollowOnContent}
                />
              );
              break;
            }
            case INPUT_TYPE.TEXT_WARNING: {
              inputHtml = <div className="input-text-warning">{item.label}</div>;
              break;
            }
            default: {
              break;
            }
          }
        }

        return inputHtml;
      })}
    </div>
  );

  return (
    <Modal
      centered
      className={classnames('middle-modal', className)}
      isOpen={isOpen}
      // TODO: zIndex
      // zIndex={1071}
    >
      <div className="custom-modal-header-container">
        <span className="custom-modal-header">{header}</span>
        <IconButton
          isFilled
          iconComponent={<img src={CloseIcon} alt="Close icon" />}
          disabled={isSaving}
          onClick={onClickCloseButton}
        />
      </div>
      <div className="custom-modal-body-container">
        {messageWarning && <div className="message-warning">{messageWarning}</div>}
        {inputListRenderer(
          inputList,
          {
            isTextareaInput,
            isHintComment,
          },
          isSaving,
          isMultiStep,
          state.step,
        )}
      </div>
      <div
        className={classnames(
          'custom-modal-footer-container',
          hasLeftButton ? '--justify-between' : '',
        )}
      >
        {isMultiStep && state.step > 0 && (
          <NormalButton buttonName="Back" onClick={onClickBack} className="me-3" />
        )}
        {hasLeftButton && (
          <NormalButton
            className="--red"
            buttonName={leftButtonName}
            onClick={onClickLeftButton}
            disabled={isMultiStep ? !isSavePermissible && state.step === 1 : !isSavePermissible}
          />
        )}
        <NormalButton
          buttonName={buttonName || (isDifferent ? 'Next' : 'Save')}
          isFilled
          disabled={isMultiStep ? !isSavePermissible && state.step === 1 : !isSavePermissible}
          isSaving={isSaving}
          onClick={handleClickSave}
          className="save-button"
        />
      </div>
    </Modal>
  );
};

export default EditModal;
